import { createElement, useCallback, useMemo, useState } from 'react';
import type { DataTableColumn } from '@/shared/components/data-table';
import { PhotoThumb } from '../components/PhotoThumb';
import { InstallGuideBadge } from '../components/InstallGuideBadge';
import { EQUIPMENT_KIND_LABELS, EQUIPMENT_KINDS } from '../types';
import type { EquipmentModelRow } from '../types';

export type EquipmentRenderContext = Record<string, never>;

/** `sortNumeric` compares getValue() as numbers (sensor and document counts). */
export type EquipmentColumnDef = DataTableColumn<EquipmentModelRow, EquipmentRenderContext> & { sortNumeric?: boolean };

const VISIBILITY_KEY = 'atek-equipment-column-visibility';
const FILTERS_KEY = 'atek-equipment-column-filters';

function loadVisibility(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(VISIBILITY_KEY) || '{}');
  } catch {
    return {};
  }
}

function loadColumnFilters(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(FILTERS_KEY) || '{}');
  } catch {
    return {};
  }
}

const GUIDE_LABELS = { verified: 'Verified', partly: 'Partly', none: 'None' } as const;

function buildColumns(): EquipmentColumnDef[] {
  return [
    {
      key: 'photo',
      label: 'Photo',
      noFilter: true,
      getValue: () => '',
      render: (item) => createElement(PhotoThumb, { photoId: item.thumbnailPhotoId, alt: item.label }),
    },
    {
      key: 'brand',
      label: 'Brand',
      sortKey: 'brand',
      filterType: 'text',
      isPrimarySearch: true,
      searchPlaceholder: 'Search brand or model...',
      getValue: (item) => `${item.brand} ${item.model} ${item.label}`,
      render: (item) => createElement('div', { className: 'min-w-0' },
        createElement('div', { className: 'font-medium truncate' }, item.label),
        createElement('div', { className: 'text-xs text-muted-foreground truncate' }, item.brand)),
    },
    {
      key: 'model',
      label: 'Model',
      sortKey: 'model',
      filterType: 'text',
      getValue: (item) => item.model,
    },
    {
      key: 'kind',
      label: 'Type',
      sortKey: 'kind',
      filterType: 'select',
      filterOptions: [...EQUIPMENT_KINDS.map((kind) => ({ value: kind, label: EQUIPMENT_KIND_LABELS[kind] })), { value: 'none', label: 'Not set' }],
      getValue: (item) => item.kind ?? 'none',
      render: (item) => item.kind
        ? createElement('span', { className: 'text-sm' }, EQUIPMENT_KIND_LABELS[item.kind] ?? item.kind)
        : createElement('span', { className: 'text-sm text-muted-foreground' }, 'Not set'),
    },
    {
      key: 'sensors',
      label: 'ATEK sensors',
      sortKey: 'sensors',
      sortNumeric: true,
      noFilter: true,
      getValue: (item) => String(item.sensorCount),
      className: 'text-right tabular-nums',
    },
    {
      key: 'docs',
      label: 'Docs',
      sortKey: 'docs',
      sortNumeric: true,
      noFilter: true,
      getValue: (item) => String(item.documentCount),
      className: 'text-right tabular-nums',
    },
    {
      key: 'guide',
      label: 'Install guide',
      sortKey: 'guide',
      filterType: 'select',
      filterOptions: Object.entries(GUIDE_LABELS).map(([value, label]) => ({ value, label })),
      getValue: (item) => item.installGuideStatus,
      render: (item) => createElement(InstallGuideBadge, { status: item.installGuideStatus }),
    },
  ];
}

function toCsvCell(value: string) {
  let text = value;
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function exportRows(items: EquipmentModelRow[], columns: EquipmentColumnDef[], ctx: EquipmentRenderContext, separator: string) {
  const cols = columns.filter((c) => c.key !== 'photo');
  const cell = (value: string) => (separator === ',' ? toCsvCell(value) : value);
  const header = cols.map((c) => cell(c.key === 'brand' ? 'Label' : c.label)).join(separator);
  const rows = items.map((item) => cols.map((c) => cell(c.key === 'brand' ? item.label : c.getValue(item, ctx))).join(separator));
  return [header, ...rows].join('\n');
}

export function exportEquipmentCSV(items: EquipmentModelRow[], columns: EquipmentColumnDef[], ctx: EquipmentRenderContext) {
  const blob = new Blob([exportRows(items, columns, ctx, ',')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `equipment_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function copyEquipmentToClipboard(items: EquipmentModelRow[], columns: EquipmentColumnDef[], ctx: EquipmentRenderContext) {
  return navigator.clipboard.writeText(exportRows(items, columns, ctx, '\t'));
}

export function useEquipmentListTable(items: EquipmentModelRow[]) {
  const [sortKey, setSortKey] = useState<string>('sensors');
  const [sortDirection, setSortDirection] = useState<1 | -1>(-1);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(loadColumnFilters);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(loadVisibility);
  const ctx = useMemo<EquipmentRenderContext>(() => ({}), []);
  const columns = useMemo(() => buildColumns(), []);

  const isVisible = useCallback((key: string) => columnVisibility[key] !== false, [columnVisibility]);
  const visibleColumns = useMemo(() => columns.filter((col) => columnVisibility[col.key] !== false), [columns, columnVisibility]);

  const toggleColumn = useCallback((key: string) => {
    setColumnVisibility((prev) => {
      const next = { ...prev, [key]: prev[key] === false };
      localStorage.setItem(VISIBILITY_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const setColumnFilter = useCallback((key: string, value: string) => {
    setColumnFilters((prev) => {
      const next = { ...prev, [key]: value };
      localStorage.setItem(FILTERS_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const filteredItems = useMemo(() => {
    const active = Object.entries(columnFilters).filter(([, value]) => value);
    if (!active.length) return items;
    return items.filter((item) => active.every(([key, value]) => {
      const col = columns.find((c) => c.key === key);
      if (!col || col.noFilter) return true;
      const raw = col.getValue(item, ctx);
      if (col.filterType === 'select') {
        try {
          const selected = JSON.parse(value) as string[];
          return selected.length === 0 || selected.includes(raw);
        } catch {
          return true;
        }
      }
      return raw.toLowerCase().includes(value.toLowerCase());
    }));
  }, [items, columnFilters, columns, ctx]);

  const sortedItems = useMemo(() => {
    const col = columns.find((c) => c.sortKey === sortKey);
    if (!col) return filteredItems;
    return [...filteredItems].sort((a, b) => {
      const av = col.getValue(a, ctx);
      const bv = col.getValue(b, ctx);
      const cmp = col.sortNumeric ? Number(av) - Number(bv) : av.localeCompare(bv, undefined, { sensitivity: 'base' });
      return (cmp || a.label.localeCompare(b.label)) * sortDirection;
    });
  }, [filteredItems, sortKey, sortDirection, columns, ctx]);

  const handleSort = useCallback((key: string) => {
    if (sortKey === key) setSortDirection((prev) => (prev === 1 ? -1 : 1));
    else {
      setSortKey(key);
      setSortDirection(columns.find((c) => c.sortKey === key)?.sortNumeric ? -1 : 1);
    }
  }, [sortKey, columns]);

  return {
    sortKey,
    sortDirection,
    columnFilters,
    columns,
    visibleColumns,
    ctx,
    sortedItems,
    isVisible,
    toggleColumn,
    setColumnFilter,
    handleSort,
  };
}
